import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Check, Plane, Clock, AlertTriangle, Send, ExternalLink, MoreHorizontal, Star, CheckCircle2, FileText, CreditCard, ChevronRight, Upload, Paperclip, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { showPresubmitRefusal } from '@/lib/presubmit-refusal.js';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu.jsx';
import QuoteEntrySheet from '@/components/sheets/QuoteEntrySheet.jsx';
import PaperDraftsSheet from '@/components/sheets/PaperDraftsSheet.jsx';
import PaymentRequestSheet from '@/components/sheets/PaymentRequestSheet.jsx';
import { getDealPaperwork } from '@/services/paperwork.service.js';
import { DOCUMENTS_CHANGED_EVENT, LEDGER_CHANGED_EVENT } from '@/services/deals.service.js';
import { deleteQuote, presentQuote } from '@/services/quotes.service.js';
import { listDealMarkets } from '@/services/markets.service.js';
import { DECLINE_REASONS, requiresDetail, declineLabels } from '@/lib/declineReasons.js';
import useDeal from '@/hooks/useDeal.js';
import useSubmissionBuilder from '@/hooks/useSubmissionBuilder.js';
import {
  createSubmission,
  cloneScenario,
  patchRoster,
  materializeScenario,
  runIatQuote,
  runGlobalQuote,
  createScenarioQuote,
  patchScenarioQuote,
  bindScenarioQuote,
  patchQuoteShopping,
  dropQuotePdf,
  getCarrierReadiness,
  landGlobalSelection,
} from '@/services/submission.service.js';
import Panel from './Panel.jsx';
import ReadinessDialog from './ReadinessDialog.jsx';
import GlobalLadderDialog from './GlobalLadderDialog.jsx';
import { toDate, formatHumanDate, formatDateOrEmpty } from '@/lib/date-display.js';
import { formatLiability } from '@/lib/liability.js';
import { openDocument } from '@/lib/openDocument.js';

// Submission Cockpit - Task L.1 spreadsheet shopping view (ref
// design-reference/submission-cockpit-spreadsheet.preview.html). Markets are
// rows in one grid, sorted by premium; premium is an inline editable cell (type
// to log); Present is a checkbox column feeding "Present selected"; the lowest
// price leads (accent + star) with a "Delta vs lowest" column; broker-requested
// alternates nest as sub-rows; declines recede in-grid with their reason; the
// row menu holds the occasionals (alternate, 6-mo, log decline, bind). Data
// model unchanged: markets / quotes / alternates from the scenario feed.
//
// Settled patterns (do not regress): all components at module scope; all hooks
// before any conditional return; visibility unconditional (status is a badge,
// never a render gate); no native browser dialogs, shadcn only; NO em
// dashes in any copy. Color language: Quoted -> success, Pending -> outline,
// Subject-to / Referred -> warning (a flag, never a lane), Declined -> text-text-dim
// (no strike), Bind -> accent. Crimson is the leader / bind action only.
//
// DEFERRED from L.1 (flagged for review, need backend / coordinated change):
//   * pilot membership value rename rated -> approved (backend seed + validate +
//     bind filter + live jsonb migration); UI copy says "Approved", stored value
//     stays 'rated' so the bind writeback keeps matching.
//   * off-applied flag (needs the quote's actual coverage in the feed).
//   * drop-PDF auto-extract + gap-scoped Open (need upload backend / sheet work);
//     drop + Open both open the quote editor for the market for now.

const SHOP = {
  not_sent: { label: 'Not sent', variant: null, pending: true },
  pending: { label: 'Pending', variant: 'outline', icon: Clock, pending: true },
  quoted: { label: 'Quoted', variant: 'success', icon: Check, priced: true },
  // Referred = priced but flagged to an underwriter (e.g. IAT "UW Review"),
  // so it can NOT be bound as-is. Amber, never green — a price you can't use yet.
  referred: { label: 'Referred', variant: 'warning', icon: AlertTriangle, priced: true, referred: true },
  selected: { label: 'Selected', variant: 'success', icon: Check, priced: true },
  declined: { label: 'Declined', variant: null, declined: true },
  expired: { label: 'Expired', variant: 'muted' },
  lost: { label: 'Lost', variant: 'muted' },
};
function shopOf(quote) {
  if (!quote) return SHOP.not_sent;
  // Legacy rows carry bound state on quotes.status only (pre-inversion) — a
  // missing shopping_status must not let a bound quote read as live 'Quoted'.
  const legacyBound = !quote.shopping_status && (quote.status === 'bound' || quote.status === 'selected');
  let key = quote.shopping_status || (legacyBound ? quote.status : (quote.premium != null ? 'quoted' : 'pending'));
  if (key === 'bound') key = 'selected';
  // A referred quote is priced but unbindable — surface it in the status lane
  // (amber) rather than letting it read as a clean green 'Quoted'.
  if (quote.referred && key === 'quoted') return SHOP.referred;
  return SHOP[key] || SHOP.pending;
}

// Resolved lanes don't age — once a market is selected/declined/expired/lost
// there's nothing left to chase.
const TERMINAL_SHOP = new Set(['selected', 'bound', 'declined', 'expired', 'lost']);
// How long a market has sat since it went to market (item 4). Neutral under 2
// days, amber at 2, red at 4 — David's >2d stale / >4d past due, the same
// cadence as the carrier.submission_stale follow-up. null when there's no date
// or the market is resolved.
function marketAging(quote) {
  if (!quote?.went_to_market_at) return null;
  if (TERMINAL_SHOP.has(quote.shopping_status)) return null;
  const sent = toDate(quote.went_to_market_at);
  if (isNaN(sent)) return null;
  const days = Math.floor((Date.now() - sent.getTime()) / 86400000);
  if (days < 0) return null;
  const tone = days >= 4 ? 'text-red-400' : days >= 2 ? 'text-warning' : 'text-text-dim';
  return { days, tone };
}

function fmtMoney(n) {
  if (n == null || !Number.isFinite(Number(n))) return null;
  return '$' + Number(n).toLocaleString('en-US');
}
function fmtDelta(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  return (v >= 0 ? '+$' : '-$') + Math.abs(v).toLocaleString('en-US');
}
function fmtCompact(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  if (v >= 1_000_000 && v % 1_000_000 === 0) return `$${v / 1_000_000}M`;
  if (v >= 1_000 && v % 1_000 === 0) return `$${v / 1_000}K`;
  return '$' + v.toLocaleString('en-US');
}
function parseMoney(s) {
  if (s == null) return null;
  const n = Number(String(s).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}
const termLabel = (m) => (Number(m) === 6 ? '6-mo' : `${m || 12}-mo`);
// Terse coverage line for the header / alt sub-row: "hull $150K · $1M/$100K".
function coverageLine(cov = {}, aircraftHull = []) {
  const parts = [];
  const hull = aircraftHull.length === 1 ? fmtCompact(aircraftHull[0].hull_value) : null;
  if (hull) parts.push(`hull ${hull}`);
  else if (aircraftHull.length > 1) parts.push(`${aircraftHull.length} hulls`);
  // The liability through the one passenger answer (lib/liability.js formatLiability): a stated $0 is passengers excluded.
  const lia = cov.liability_per_occurrence != null ? formatLiability({ liability_per_occurrence: cov.liability_per_occurrence, liability_per_passenger: cov.liability_per_passenger }) : '';
  if (lia) parts.push(lia);
  return parts.join(' · ');
}

// Seed the no-app renewal bind's summary-of-changes from what the submission
// knows: named exclusions ("Pilot X removed") plus off-applied terms on the
// winning quote ("hull $125K"). The broker edits before send; the text flows
// into what_youre_paying_for and the client acknowledgment.
function buildNoAppSeed(target, submissionPilots) {
  const bits = [];
  for (const p of (submissionPilots || [])) {
    if (p?.membership === 'excluded' && (p.name || '').trim()) bits.push(`Pilot ${p.name.trim()} removed`);
  }
  const off = target?.quote ? offAppliedTerms(target.quote, target.scenario) : [];
  if (off.length) bits.push(`Terms changed: ${off.join(', ')}`);
  return bits.length
    ? `${bits.join('; ')}; otherwise same terms as the expiring policy.`
    : 'Same terms as the expiring policy.';
}

// True when the quote is the selected / bound one — the quote IS the policy at
// that point, so the off-applied comparison is meaningless (the as-applied
// terms were superseded by what was actually bound).
function isSelectedOrBound(quote, scenario) {
  return quote?.is_bound === true
    || quote?.shopping_status === 'selected' || quote?.shopping_status === 'bound'
    || quote?.status === 'selected' || quote?.status === 'bound'
    || scenario?.is_bound === true;
}

// Off-applied detection (L.4): compare the carrier's ACTUAL quoted coverage
// (quote.quoted_coverage, from the feed) against the as-applied-for scenario
// terms. Returns short labels for the fields that came back DIFFERENT
// ("hull $125K", "$1M/$50K", "med $5K") so the grid can flag "came back off
// applied" without opening the quote. Conservative: a field is only flagged
// when BOTH sides carry a value (missing on either side → no flag, so sparse
// data never produces false positives).
//
// DEDUCTIBLES ARE EXCLUDED ON PURPOSE: deductibles are carrier-set — carriers
// routinely quote their own regardless of what we applied for, so comparing
// them flags nearly every quote (false signal, "crying wolf"). Only the terms
// we genuinely apply for — hull value, liability limits, medical payments — are
// real signal when a carrier deviates.
function offAppliedTerms(quote, scenario) {
  const qc = quote?.quoted_coverage;
  if (!qc) return [];
  // The flag is meaningless on a selected/bound quote (the quote is the policy).
  if (isSelectedOrBound(quote, scenario)) return [];
  const cov = scenario?.coverage || {};
  const num = (v) => (v == null || v === '' ? null : Number(String(v).replace(/[^0-9.]/g, '')));
  const eqNum = (a, b) => { const na = num(a), nb = num(b); return na == null || nb == null ? true : na === nb; };
  const out = [];
  // Hull only when the scenario applied a single hull — a one-line flag can't
  // express a multi-aircraft hull schedule (the broker opens the quote for that).
  const appliedHull = (scenario?.aircraft_hull || []).length === 1 ? scenario.aircraft_hull[0].hull_value : null;
  if (appliedHull != null && qc.hull_value != null && !eqNum(qc.hull_value, appliedHull)) {
    out.push(`hull ${fmtCompact(qc.hull_value)}`);
  }
  if (!eqNum(qc.liability_per_occurrence, cov.liability_per_occurrence) || !eqNum(qc.liability_per_passenger, cov.liability_per_passenger)) {
    // Through the one passenger answer (lib/liability.js formatLiability): a stated $0 is passengers excluded.
    const lia = qc.liability_per_occurrence != null ? formatLiability({ liability_per_occurrence: qc.liability_per_occurrence, liability_per_passenger: qc.liability_per_passenger }) : '';
    if (lia) out.push(lia);
  }
  if (!eqNum(qc.medical_payments, cov.medical_payments)) {
    const m = fmtCompact(qc.medical_payments);
    if (m) out.push(`med ${m}`);
  }
  return out;
}

// THE DECLINE TAXONOMY IS NOT DECLARED HERE ANY MORE (CC PROMPT 23 Part A).
// It was: ten values in this component, writing quotes.decline_category, while
// the market ladder's dialog wrote a different set of five into
// submission_markets. Two vocabularies for one fact, each declared in the file
// that happened to need it, and 'pilot_experience' meant something subtly
// different on each side.
//
// The vocabulary is now DATA (data/decline-reasons.json), mirrored for the
// browser in @/lib/declineReasons.js and pinned to the JSON by the deploy gate.
// The reason lands on submission_markets through the one decline door.

function cobrokerName(slug, registryBySlug) {
  const n = registryBySlug.get(slug)?.name || slug || '';
  return n.replace(/\s+Aviation.*$/i, '').trim() || (slug || '').toUpperCase();
}
function channelLabel(reg, registryBySlug, viaOverride) {
  if (viaOverride) return `via ${cobrokerName(viaOverride, registryBySlug)}`;
  if (!reg) return 'Direct';
  if (reg.routing_type === 'api') return 'API';
  if (reg.routing_type === 'via_cobroker') return `via ${cobrokerName(reg.cobroker_slug, registryBySlug)}`;
  if (reg.routing_type === 'portal') return 'Portal';
  return 'Direct';
}

// ── Roster strip ────────────────────────────────────────────────────────────
function PilotChip({ pilot, name, busy, onApprove, onExcludeStart }) {
  const excluded = pilot.membership === 'excluded';
  // A name-only pilot (no linked CRM Contact) rides the roster but can't be
  // excluded (exclusions key on a contact) and blocks bind until linked.
  const unlinked = !pilot.contact_id;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" disabled={busy} className="disabled:opacity-50"
          title={excluded ? `Named excluded${pilot.exclude_reason ? ` (${pilot.exclude_reason})` : ''}` : (unlinked ? 'Name-only pilot, link a CRM Contact before binding' : 'Approved pilot')}>
          <Badge variant={excluded ? 'dashed' : (unlinked ? 'warning' : 'success')} className="cursor-pointer gap-1">
            {excluded ? null : <Check size={12} strokeWidth={2.2} />}
            {name}
            {excluded ? <span className="text-[9px] uppercase tracking-wide"> excl</span> : null}
            {unlinked && !excluded ? <span className="text-[9px] uppercase tracking-wide"> link</span> : null}
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wide">{name}</DropdownMenuLabel>
        {unlinked ? (
          <DropdownMenuLabel className="text-[10px] font-normal normal-case text-warning">
            Link a CRM Contact before binding.
          </DropdownMenuLabel>
        ) : null}
        <DropdownMenuItem disabled={!excluded} onSelect={() => onApprove(pilot)}>Approved (on the quote)</DropdownMenuItem>
        <DropdownMenuItem disabled={excluded || unlinked} onSelect={() => onExcludeStart(pilot)}>Named exclusion…</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
function AircraftChip({ aircraft, busy, onToggle }) {
  const included = aircraft.included !== false;
  return (
    <button type="button" disabled={busy} onClick={() => onToggle(aircraft)} className="disabled:opacity-50"
      title={included ? 'On the schedule, click to drop' : 'Off the schedule, click to include'}>
      <Badge variant={included ? 'info' : 'dashed'} className="cursor-pointer gap-1 font-mono">
        {included ? <Plane size={12} strokeWidth={2} /> : null}{aircraft.tail_number}
      </Badge>
    </button>
  );
}
function RosterStrip({ submission, nameByContact, busy, onApprove, onExcludeStart, onToggleAircraft }) {
  const pilots = submission?.pilots || [];
  const aircraft = submission?.aircraft || [];
  return (
    <div className="mb-3 mt-3 flex flex-wrap items-center gap-1.5 rounded-card bg-surface-strong px-2.5 py-2">
      <span className="mr-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-dim">Roster</span>
      {pilots.map((p) => (
        <PilotChip key={p.roster_key || p.contact_id || p.phf_id} pilot={p}
          name={p.name
            || (p.contact_id ? nameByContact.get(String(p.contact_id)) : null)
            || (p.contact_id ? `Contact …${String(p.contact_id).slice(-4)}` : 'Name-only pilot')}
          busy={busy} onApprove={onApprove} onExcludeStart={onExcludeStart} />
      ))}
      {aircraft.map((a) => <AircraftChip key={a.tail_number} aircraft={a} busy={busy} onToggle={onToggleAircraft} />)}
      {pilots.length === 0 && aircraft.length === 0
        ? <span className="text-xs italic text-text-dim">Empty roster (seed failed?)</span>
        : <span className="ml-auto text-xs text-text-dim">included · 12-mo unless noted</span>}
    </div>
  );
}

// ── Premium cell (inline editable; type to log) ──────────────────────────────
// Local state so a background refresh never clobbers what is being typed; commits
// on blur / Enter. Module scope (never redefined per render).
function PremiumCell({ row, busy, onCommit }) {
  const priced = row.quote?.premium != null;
  // Referred premium reads amber, not the default near-white — it's a number
  // you can't bind on yet, so it shouldn't look like a usable quote.
  const referred = row.quote?.referred === true;
  const [val, setVal] = useState(priced ? fmtMoney(row.quote.premium) : '');
  const [editing, setEditing] = useState(false);
  const display = editing ? val : priced ? fmtMoney(row.quote.premium) : '';

  function commit() {
    setEditing(false);
    const n = parseMoney(val);
    if (n == null) { setVal(priced ? fmtMoney(row.quote.premium) : ''); return; }
    if (priced && n === Number(row.quote.premium)) return;
    onCommit(row, n);
  }
  if (row.declined) return <span className="text-text-dim">·</span>;
  return (
    <input
      className={[
        'w-full min-w-[6rem] rounded-[5px] border border-transparent bg-transparent px-1.5 py-1 text-right font-mono text-sm font-semibold hover:border-border focus:border-accent focus:bg-bg focus:outline-none disabled:opacity-50',
        referred ? 'text-warning' : 'text-text',
      ].join(' ')}
      value={display}
      // Empty cell reads as a STATE ("pending"), not an instruction ("log").
      // A market in UW review with no number shows the same. Declined markets
      // never reach here — they return a dim dot above. The title keeps the
      // drop/type affordance discoverable now that the placeholder isn't a verb.
      placeholder="pending"
      title="Type a premium or drop a PDF to log it"
      disabled={busy}
      onFocus={() => { setEditing(true); setVal(priced ? fmtMoney(row.quote.premium) : ''); }}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setEditing(false); e.currentTarget.blur(); } }}
    />
  );
}

// ── Row menu (the occasionals) ───────────────────────────────────────────────
function RowMenu({ row, market, busy, handlers }) {
  const { scenario, quote, isBase } = row;
  const priced = shopOf(quote).priced && quote?.premium != null;
  // A referred quote can NOT be bound — it isn't firm until the underwriter
  // clears it, so the Bind action is withheld entirely (no menu item). Same
  // for a quote that already IS the policy (selected/bound, including legacy
  // quotes.status='bound'): Bind never renders on it. DEAL-LIFECYCLE-SPEC
  // Phase 0 — this exact gap let a broker re-bind a bound, paid deal.
  const canBind = priced && quote?.crm_quotation_id && !quote?.referred && !isSelectedOrBound(quote, scenario);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" disabled={busy} aria-label="Row actions"
          className="grid h-7 w-7 place-items-center rounded text-text-dim hover:bg-surface-strong hover:text-text disabled:opacity-50">
          <MoreHorizontal size={16} strokeWidth={1.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {isBase ? (
          <>
            <DropdownMenuItem onSelect={() => handlers.onAlternate(market)}>Add requested alternate</DropdownMenuItem>
          </>
        ) : null}
        {market.reg?.routing_type === 'api' && !quote ? (
          <DropdownMenuItem onSelect={() => handlers.onEdit(scenario, market.reg, quote)}>Enter quote manually</DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => handlers.onEdit(scenario, market.reg, quote)}>Mark off-applied terms…</DropdownMenuItem>
        {quote ? (
          <DropdownMenuItem onSelect={() => handlers.onSetMarketDate(scenario, market, quote)}>Set sent date…</DropdownMenuItem>
        ) : null}
        {quote && !shopOf(quote).declined ? (
          <DropdownMenuItem onSelect={() => handlers.onDecline(scenario, quote)}>Log decline</DropdownMenuItem>
        ) : null}
        {canBind ? <DropdownMenuItem onSelect={() => handlers.onBind(scenario, market.reg, quote)}>Bind…</DropdownMenuItem> : null}
        {quote ? (
          <DropdownMenuItem onSelect={() => handlers.onDelete(scenario, market, quote)} className="gap-1.5 text-text-muted focus:text-text">
            <Trash2 size={13} strokeWidth={1.7} />Delete quote…
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// "Doc on file" marker — a carrier PDF was dropped + filed to this quote (L.4).
// Clicks through to the WorkDrive permalink; shows the count when >1.
function DocOnFile({ quote }) {
  if (!quote?.has_document) return null;
  const n = quote.document_count || 1;
  const label = n > 1 ? `${n} docs on file` : 'doc on file';
  const inner = (
    <span className="inline-flex items-center gap-0.5 text-info" title={label}>
      <Paperclip size={11} strokeWidth={2} />{n > 1 ? <span className="text-[10px]">{n}</span> : null}
    </span>
  );
  return quote.document_permalink
    ? <button type="button" onClick={(e) => { e.stopPropagation(); openDocument(quote.document_permalink); }} aria-label={label}>{inner}</button>
    : inner;
}

// ── Spreadsheet row ──────────────────────────────────────────────────────────
function MarketRow({ row, market, lowest, isLeader, busy, handlers, declination = null }) {
  const { scenario, quote, isBase } = row;
  const s = shopOf(quote);
  const Icon = s.icon;
  const priced = s.priced && quote?.premium != null;
  const delta = priced && lowest != null ? Number(quote.premium) - lowest : null;
  const isLowest = priced && lowest != null && Number(quote.premium) === lowest;
  const offApplied = priced ? offAppliedTerms(quote, scenario) : [];
  const aging = marketAging(quote);

  function onDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handlers.onDropPdf(scenario, market, quote, file);
  }

  return (
    <tr
      className={[
        s.declined ? 'opacity-60' : '',
        isLeader ? 'bg-accent-soft' : !isBase ? 'bg-white/[0.012]' : '',
      ].join(' ')}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      {/* Market */}
      <td className="border-t border-border px-2.5 py-2 align-middle">
        {isBase ? (
          <div className="flex items-center gap-1.5 font-medium text-text">
            {isLeader ? <Star size={13} className="text-accent" fill="currentColor" strokeWidth={0} /> : null}
            <span className="truncate">{market.name}</span>
            <span className="text-[11px] font-normal text-text-dim">· {market.channel}</span>
            <DocOnFile quote={quote} />
          </div>
        ) : (
          <div className="flex items-center gap-1.5 pl-4 text-text-muted">
            <span className="font-mono text-text-dim">└</span>
            <span className="text-[10px] uppercase tracking-wide text-info">alt requested</span>
            <span className="truncate text-xs">· {coverageLine(scenario.coverage, scenario.aircraft_hull) || scenario.scenario_label}</span>
            <DocOnFile quote={quote} />
          </div>
        )}
      </td>

      {/* Status + how long the market has sat (item 4) */}
      <td className="border-t border-border px-2.5 py-2 align-middle">
        <div className="flex items-center gap-1.5">
          {s.variant
            ? <Badge variant={s.variant} className="gap-1">{Icon ? <Icon size={11} strokeWidth={2.2} /> : null}{s.label}</Badge>
            : <span className="text-xs text-text-dim">{s.label}</span>}
          {aging ? (
            <span className={`font-mono text-[11px] ${aging.tone}`} title={`Went to market ${quote.went_to_market_at}`}>
              · {aging.days}d
            </span>
          ) : null}
        </div>
      </td>

      {/* Premium (inline) */}
      <td className="whitespace-nowrap border-t border-border px-2.5 py-2 text-right align-middle">
        <PremiumCell row={{ ...row, market, declined: s.declined }} busy={busy} onCommit={handlers.onPremium} />
      </td>

      {/* Delta vs lowest */}
      <td className="border-t border-border px-2.5 py-2 text-right align-middle font-mono text-xs">
        {s.declined || !priced ? <span className="text-text-dim">·</span>
          : isLowest ? <span className="text-success">lowest</span>
          : <span className="text-text-dim">{fmtDelta(delta)}</span>}
      </td>

      {/* Terms / conditions */}
      <td className="border-t border-border px-2.5 py-2 align-middle text-xs">
        {s.declined ? (
          <span className="text-text-dim" title={declination?.detail || undefined}>
            {declineLabels(declination).join(', ') || 'declined'}
          </span>
        ) : !priced ? (
          <span className="inline-flex items-center gap-1 text-text-dim">
            drop the quote PDF or type a premium
          </span>
        ) : (
          <div className="flex flex-col items-start gap-0.5">
            {/* Came back off applied: the carrier quoted terms different from
                what we asked for (auto-detected from the feed coverage). */}
            {offApplied.length ? (
              <span className="inline-flex max-w-full items-center gap-1 rounded-pill bg-warning-soft px-2 py-0.5 text-warning"
                title={`Off applied: ${offApplied.join(' · ')}`}>
                <AlertTriangle size={11} strokeWidth={2} className="shrink-0" />
                <span className="min-w-0 truncate">off applied: {offApplied.join(' · ')}</span>
              </span>
            ) : !quote?.subject_to ? (
              <span className="text-text-dim">as applied</span>
            ) : null}
            {quote?.subject_to ? (
              <span className="inline-flex items-center gap-1 rounded-pill bg-warning-soft px-2 py-0.5 text-warning">
                <AlertTriangle size={11} strokeWidth={2} />subject to: {quote.subject_to}
              </span>
            ) : null}
            {/* "Referred" lives in the Status lane now (amber badge), not here. */}
          </div>
        )}
      </td>

      {/* Present checkbox */}
      <td className="border-t border-border px-2.5 py-2 text-center align-middle">
        {priced && !s.declined ? (
          <button type="button" disabled={busy} aria-label={quote.presenting ? 'Presenting' : 'Present'}
            onClick={() => handlers.onPresent(scenario, quote)}
            className={[
              'inline-grid h-4 w-4 place-items-center rounded border align-middle',
              quote.presenting ? 'border-success bg-success text-[#06210f]' : 'border-border-strong text-transparent hover:border-text-dim',
            ].join(' ')}>
            <Check size={11} strokeWidth={3} />
          </button>
        ) : null}
      </td>

      {/* Actions. An api-routing market with no quote yet leads with an inline
          "Run API quote" submit button (firing IAT right from the row); manual
          entry moves to the row menu. Every other row keeps "Open". */}
      <td className="border-t border-border px-2.5 py-2 text-right align-middle">
        <div className="flex items-center justify-end gap-1.5">
          {market.reg?.routing_type === 'api' && !quote ? (
            <button type="button" disabled={busy} onClick={() => handlers.onMaterialize(scenario, market.reg)}
              className="inline-flex items-center gap-1 rounded-md border border-border-strong bg-surface-strong px-2.5 py-1 text-[11px] font-medium text-text hover:border-text-dim hover:bg-surface disabled:opacity-50">
              <Send size={12} strokeWidth={1.8} />Run API quote
            </button>
          ) : s.declined ? null : (
            <button type="button" disabled={busy} onClick={() => handlers.onEdit(scenario, market.reg, quote)}
              className="rounded-md border border-border-strong px-2.5 py-1 text-[11px] text-text-muted hover:bg-surface-strong hover:text-text disabled:opacity-50">
              Open
            </button>
          )}
          <RowMenu row={row} market={market} busy={busy} handlers={handlers} />
        </div>
      </td>
    </tr>
  );
}

// ── Bound state (L.2) ────────────────────────────────────────────────────────
// The client has selected (and, once paid, signed + paid) in the wizard. Collapse
// to the selected quote; other markets recede into a "N kept" line (retained for
// record + appetite, not deleted); the broker then places with the carrier.
// The route descriptor stored as placement_via (audit of HOW it was placed).
// Kept separate from the button label so the label can read honestly without
// the stored value losing the api/portal/email detail.
function placeRoute(reg) {
  if (!reg) return 'carrier';
  if (reg.routing_type === 'api') return `${reg.name} (API)`;
  if (reg.routing_type === 'portal') return `${reg.name} (portal)`;
  if (reg.routing_type === 'via_cobroker') return `co-broker (email)`;
  return `${reg.name} (email)`;
}
// Button / dialog label. NO send verb and NO "(API)" affordance — the transmit
// is a deferred follow-on (§3b), so this only RECORDS that the broker placed the
// risk. Reads "Mark placed with {carrier}" so it never looks like a carrier send.
function placeLabel(reg) {
  if (!reg) return 'Mark placed';
  return `Mark placed with ${reg.name}`;
}
function DocChip({ Icon, label, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-strong px-2.5 py-1.5 text-xs text-text hover:border-border-strong">
      <Icon size={13} strokeWidth={1.7} className="text-text-muted" />{label}
    </button>
  );
}
function BoundState({ dealLabel, appliedLine, selected, registryBySlug, approvedNames, otherCounts, busy, onPlace, onExpand, onDoc, onOpenDocs, artifacts }) {
  const { reg, quote, scenario } = selected;
  const placed = quote.placement_status === 'placed';
  const terms = [coverageLine(scenario.coverage, scenario.aircraft_hull), termLabel(quote.term_months)].filter(Boolean).join(' · ');
  const bundleOk = !!artifacts?.bundle?.ok;
  // The signed application is the window's answer (paperwork-1 round 2, David Q4: one answer everywhere), its words the server's.
  const sa = artifacts?.signedApplication || null;
  const hasSignedApp = sa?.state === 'in_window' && !!sa.permalink;
  return (
    <>
      <div className="flex items-center gap-2">
        <div className="text-[17px] font-medium text-text">{dealLabel}</div>
        <span className="rounded-pill bg-success-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-success">Bound</span>
      </div>
      {appliedLine ? (
        <div className="mt-0.5 text-sm text-text-muted">
          <span className="mr-1.5 text-[10px] uppercase tracking-[0.1em] text-text-dim">As applied for</span>{appliedLine}
        </div>
      ) : null}

      <div className="my-3 flex items-center gap-2.5 rounded-card border border-success/25 bg-success-soft px-3 py-2.5 text-[13px]">
        <CheckCircle2 size={18} strokeWidth={2} className="shrink-0 text-success" />
        <div>
          {/* BoundState renders only once the deal is paid (boundView gate) —
              "bound = paid", so the copy is unconditionally the paid wording. */}
          Client selected, signed, and paid.
          <span className="text-xs text-text-muted">
            {' '}{bundleOk ? 'Binding packet ready.' : 'Paperwork pending review.'}
          </span>
        </div>
      </div>

      <div className="mb-3.5 overflow-hidden rounded-card border border-accent">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 bg-accent-soft px-3.5 py-2.5">
          <span className="text-[15px] font-semibold text-text">{reg.name}</span>
          <span className="text-xs text-text-dim">· {channelLabel(reg, registryBySlug)}</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-accent">selected</span>
          <span className="ml-auto font-mono text-base font-semibold text-text">{fmtMoney(quote.premium) || '·'}</span>
        </div>
        <div className="border-t border-border px-3.5 py-2 font-mono text-xs text-text-muted">
          {terms}{approvedNames ? ` · Approved pilots: ${approvedNames}` : ''}
        </div>
        <div className="flex flex-wrap gap-2 border-t border-border px-3.5 py-2.5">
          {/* A broker clicking a doc chip wants THE DOCUMENT — never the
              client-facing Tower URL. Absent artifacts say so honestly. */}
          {hasSignedApp ? (
            <DocChip Icon={FileText} label="Signed application" onClick={() => onDoc('application')} />
          ) : (
            <span className="inline-flex items-start gap-1.5 rounded-md border border-border/60 px-2.5 py-1.5 text-xs text-text-dim" data-signed-application-chip={sa?.state || 'unknown'}>
              <FileText size={13} strokeWidth={1.7} className="mt-0.5 shrink-0" />
              <span className="min-w-0">
                Signed application · {sa?.status || 'Could not check the signed application'}
                {sa?.state === 'out_of_window' && sa.action && typeof onOpenDocs === 'function' ? (
                  <button type="button" className="mt-1 block rounded border border-accent px-1.5 py-0.5 text-left text-[11px] text-accent hover:bg-accent-soft" onClick={onOpenDocs} data-action={sa.action.key}>{sa.action.label} (on the Paperwork tab)</button>
                ) : null}
              </span>
            </span>
          )}
          <DocChip Icon={CreditCard} label="Payment confirmation" onClick={() => onDoc('payment')} />
          <DocChip Icon={FileText} label={bundleOk ? 'Binding packet' : 'Review paperwork first'} onClick={() => onDoc('binder')} />
        </div>
        <div className="border-t border-border bg-surface-strong px-3.5 py-3">
          <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-text-dim">Place with carrier</div>
              <div className="text-[13px] text-text-muted">
                {placed
                  ? <>Placed{quote.placement_via ? ` via ${quote.placement_via}` : ''}{quote.placement_ref ? ` · ${quote.placement_ref}` : ''}</>
                  : <b className="font-medium text-warning">Not yet placed</b>}
              </div>
            </div>
            <Button variant="accent" disabled={busy} onClick={onPlace} className="w-full gap-1.5 sm:ml-auto sm:w-auto"
              title="Records placement status only, no carrier transmit yet">
              <CheckCircle2 size={14} strokeWidth={1.7} />{placed ? 'Update placement' : placeLabel(reg)}
            </Button>
          </div>
        </div>
      </div>

      <button type="button" onClick={onExpand}
        className="flex w-full flex-col gap-1 rounded-card border border-border px-3 py-2.5 text-xs text-text-dim hover:border-border-strong hover:text-text-muted sm:flex-row sm:items-center sm:gap-2.5">
        <span className="flex items-center gap-2.5"><ChevronRight size={13} strokeWidth={2} />Other markets</span>
        <span className="text-text-dim sm:ml-auto">
          {otherCounts.kept} kept · {otherCounts.quoted} quoted · {otherCounts.declined} declined · retained for record &amp; appetite
        </span>
      </button>
    </>
  );
}

// ── Distribute state (L.3, slimmed per ADDITIONAL-INTERESTS-SPEC Part C) ────
// After the carrier issues: bind summary + one status line ("4 of 5 documents
// on file, 1 interest awaiting a certificate") that links to the Paperwork tab,
// where the full checklist lives. paperwork-1 L2-1: the line counts the
// server's checklist (GET /api/tracon/deals/:dealId/paperwork, the one
// derivation); nothing here derives an item.
// Review fix M6: the counts are the server's (checklist.counts.documents / .recipients); an item that could not be checked is
// said, never counted as in hand.
function DistributeState({ dealLabel, selected, coverage, effective, invoice, paperwork, paperworkError, busy, onLedger, onExpand, onOpenDocs }) {
  const items = paperwork?.checklist?.items || [];
  const counts = paperwork?.checklist?.counts || null;
  const docs = counts?.documents || null;
  const rec = counts?.recipients || null;
  // Q10 (David 2026-10-31): Distributed means the standard set delivered; the server says whether it was.
  const delivery = counts?.delivery?.state || null;
  const policy = items.find((i) => i.key === 'policy');
  const binder = items.find((i) => i.key === 'binder');

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="text-[17px] font-medium text-text">{dealLabel}</div>
        <span className="rounded-pill bg-info-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-info">Issued</span>
      </div>
      <div className="mt-0.5 text-[13px] text-text-muted">
        <b className="font-medium text-text">{selected.reg.name}</b>
        {coverage ? <> · {coverage}</> : null}
        {effective ? <> · eff {effective}</> : null}
        {invoice ? (
          <button type="button" onClick={onLedger} className="ml-2 text-[11px] text-info hover:underline">invoice {invoice} · view in ledger</button>
        ) : null}
      </div>

      {policy?.state === 'missing' ? (
        <div className="my-3 flex items-center gap-2.5 rounded-card border border-warning/25 bg-warning-soft px-3 py-2.5 text-[13px]" data-distribute-policy="missing">
          <AlertTriangle size={18} strokeWidth={2} className="shrink-0 text-warning" />
          <div>{binder?.state === 'filed' ? 'On a binder, policy pending from carrier.' : 'The policy is not on file yet.'}</div>
        </div>
      ) : null}

      {/* The paperwork detail lives on the Paperwork tab; the card keeps a single status line that links there. */}
      <button type="button" disabled={busy} onClick={onOpenDocs} data-distribute-summary
        className="mt-3 flex w-full items-center gap-2 rounded-card border border-border px-3 py-2.5 text-left text-[13px] text-text-muted hover:border-border-strong hover:text-text">
        <FileText size={14} strokeWidth={1.7} className="shrink-0 text-text-muted" />
        <span>
          {paperwork && docs && rec ? (
            <>
              {docs.in_hand} of {docs.items} documents in hand
              {docs.unknown > 0 ? <span className="text-warning" data-distribute-unknown>{` · ${docs.unknown} could not be checked`}</span> : null}
              {rec.unknown > 0 ? <span className="text-warning" data-distribute-recipients-unknown> · the interests could not be checked</span>
                : rec.items > 0 ? <> · {rec.awaiting > 0 ? `${rec.awaiting} interest${rec.awaiting === 1 ? '' : 's'} still need${rec.awaiting === 1 ? 's' : ''} paper` : 'all interests have paper'}</> : null}
              {delivery === 'filed' ? <span data-distribute-delivery="filed"> · the standard set is delivered</span>
                : delivery === 'missing' ? <span data-distribute-delivery="missing"> · the standard set is not yet delivered</span>
                : delivery === 'unknown' ? <span className="text-warning" data-distribute-delivery="unknown"> · whether the standard set was delivered could not be checked</span> : null}
            </>
          ) : paperwork ? 'The paperwork answered without its counts. Open the Paperwork tab'
          : paperworkError ? `The paperwork could not be read: ${paperworkError}. Open the Paperwork tab` : 'Reading the paperwork'}
        </span>
        <ChevronRight size={13} strokeWidth={2} className="ml-auto shrink-0 text-text-dim" />
      </button>

      <button type="button" onClick={onExpand} className="mt-3 inline-flex items-center gap-1 text-[11px] text-text-dim hover:text-text-muted">
        <ChevronRight size={12} strokeWidth={2} />View shopping history
      </button>
    </>
  );
}

// ── Main panel ──────────────────────────────────────────────────────────────
// lob: the deal line's lines_of_business key for the Add-market picker
// (Ship 0: hangar markets were structurally invisible behind a hard-coded
// 'hull_liability' filter).
export default function SubmissionBuilderPanel({ dealId, fleetAndRoster, documents, accountId, onChange, onOpenDocs, onOpenTrust, lob = 'hull_liability' }) {
  const navigate = useNavigate();
  const { submission, scenarios, carriers, loading, error, refresh } = useSubmissionBuilder(dealId);
  const { data: dealRes } = useDeal(dealId || null);
  const [busy, setBusy] = useState(false);
  // Dispatch preflight gate: {scenario, carrier, readiness} while the broker is
  // looking at the blockers/warnings for a carrier they tried to fire.
  const [gate, setGate] = useState(null);
  // Global option ladder: {scenario, carrier, quote, quoteId, ladder, selection,
  // warnings} while the broker chooses the hull basis / liability tier. The quote
  // is captured at Global already; nothing lands on the scenario until they pick.
  const [ladder, setLadder] = useState(null);
  const [addedMarkets, setAddedMarkets] = useState([]);
  const [excludePilot, setExcludePilot] = useState(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [editorTarget, setEditorTarget] = useState(null);
  const [bindTarget, setBindTarget] = useState(null);
  const [bindDone, setBindDone] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // {scenario, market, quote} being deleted
  const [declineTarget, setDeclineTarget] = useState(null); // {scenario, quote} being declined
  const [declineReasons, setDeclineReasons] = useState([]);
  const [declineNotes, setDeclineNotes] = useState('');
  // What each market said, read from the ONE decline store rather than from the
  // quote's own columns (CC PROMPT 23 Part A). Keyed by carrier slug, which is
  // what quote.carrier holds on this surface.
  const [marketDeclines, setMarketDeclines] = useState(new Map());
  const [marketDateTarget, setMarketDateTarget] = useState(null); // {scenario, market, quote} — editing went-to-market date
  const [marketDateVal, setMarketDateVal] = useState('');
  const [presentConfirm, setPresentConfirm] = useState(false);
  const [presentReferredTarget, setPresentReferredTarget] = useState(null); // {scenario, quote} — presenting a referred quote
  const [presentReviewed, setPresentReviewed] = useState(false);            // "UW has cleared it" in that dialog
  const [placeTarget, setPlaceTarget] = useState(null);   // selected quote being placed
  const [expandFromBound, setExpandFromBound] = useState(false); // "Other markets" expanded back to the grid
  const [paperwork, setPaperwork] = useState(null);       // the server's paperwork checklist (paperwork-1 L2-1) → the status line
  const [paperworkError, setPaperworkError] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);   // payment receipt view (bound-view chip)
  const [paperDraftsOpen, setPaperDraftsOpen] = useState(false); // review-first flow for the binder chip
  // No-app renewal bind (FLOW 2026-07-11): broker-decided per deal in the
  // Bind dialog; on confirm the flow skips the binding wizard and opens the
  // renewal payment request prefilled with the summary of changes.
  const [noApp, setNoApp] = useState(false);
  const [noAppSummary, setNoAppSummary] = useState('');
  const [prOpen, setPrOpen] = useState(false);
  const [prPrefill, setPrPrefill] = useState(null);

  const dealItem = dealRes?.deal || null;
  // "Bound means paid": the derived lifecycle state (Phase 1) is the primary
  // signal; the raw CRM Stage strings remain as fallback for payloads that
  // predate the state computation.
  const dealPaid = ['bound', 'placed', 'distributed'].includes(dealItem?.lifecycle_state?.state)
    || ['Paid', 'Bound'].includes(dealItem?.deal_stage || '');
  // No-app renewal bind eligibility: Type 'Renewal Business' with a prior
  // policy on record. NEVER offered on New Business (guardrail); broker-
  // decided per deal, never automatic.
  const renewalNoAppEligible = dealItem?.deal_type === 'Renewal Business' && !!dealItem?.prior_policy_id;
  const towerUrl = dealItem?.session_token ? `https://ndemnifly.com/tower/${dealItem.session_token}` : null;

  const nameByContact = useMemo(() => {
    const m = new Map();
    for (const p of dealRes?.deal?.pilots?.details || []) if (p.crm_id && p.name) m.set(String(p.crm_id), p.name);
    for (const r of fleetAndRoster?.roster || []) if (r.crm_contact_id && r.name) m.set(String(r.crm_contact_id), r.name);
    return m;
  }, [dealRes, fleetAndRoster]);

  const registryBySlug = useMemo(() => new Map(carriers.map((c) => [c.slug, c])), [carriers]);

  const orderedScenarios = useMemo(() => {
    const byId = new Map(scenarios.map((g) => [g.scenario_id, g]));
    const children = new Map();
    const roots = [];
    for (const g of scenarios) {
      const pid = g.parent_scenario_id;
      if (pid && byId.has(pid)) {
        if (!children.has(pid)) children.set(pid, []);
        children.get(pid).push(g);
      } else roots.push(g);
    }
    const out = [];
    const walk = (node, depth) => { out.push({ scenario: node, depth }); for (const c of children.get(node.scenario_id) || []) walk(c, depth + 1); };
    roots.forEach((r) => walk(r, 0));
    return out;
  }, [scenarios]);

  const rootScenario = useMemo(() => orderedScenarios.find((x) => x.depth === 0)?.scenario || null, [orderedScenarios]);
  const quoteFor = useCallback((scenario, slug) => (scenario.quotes || []).find((q) => q.carrier === slug) || null, []);

  // Build the spreadsheet: one market per row (base), broker-requested
  // alternates nested under it, sorted by base premium ascending; pending markets
  // next; declined recede to the bottom. The lowest-priced base is the leader.
  const { tableRows, lowest, marketCount, markets } = useMemo(() => {
    const slugs = new Set(addedMarkets);
    for (const g of scenarios) for (const q of g.quotes || []) if (q.carrier) slugs.add(q.carrier);

    const markets = [];
    for (const slug of slugs) {
      const reg = registryBySlug.get(slug) || { slug, name: slug, routing_type: 'direct_email' };
      const base = { scenario: rootScenario, depth: 0, quote: rootScenario ? quoteFor(rootScenario, slug) : null, isBase: true };
      const alts = [];
      for (const { scenario, depth } of orderedScenarios) {
        if (depth === 0) continue;
        const q = quoteFor(scenario, slug);
        if (q) alts.push({ scenario, depth, quote: q, isBase: false });
      }
      const basePremium = shopOf(base.quote).priced && base.quote?.premium != null ? Number(base.quote.premium) : null;
      // A referred base quote is priced but unbindable, so it never anchors
      // "lowest" or wins the leader star — the leader must be a usable quote.
      const baseReferred = base.quote?.referred === true;
      const quotesHere = [base.quote, ...alts.map((a) => a.quote)].filter(Boolean);
      const allDeclined = quotesHere.length > 0 && quotesHere.every((q) => shopOf(q).declined);
      markets.push({ slug, reg, name: reg.name, channel: channelLabel(reg, registryBySlug), base, alts, basePremium, baseReferred, allDeclined });
    }

    const low = markets.filter((m) => !m.allDeclined && !m.baseReferred && m.basePremium != null).reduce((acc, m) => (acc == null ? m.basePremium : Math.min(acc, m.basePremium)), null);

    markets.sort((a, b) => {
      if (a.allDeclined !== b.allDeclined) return a.allDeclined ? 1 : -1;
      const ap = a.basePremium == null ? Infinity : a.basePremium;
      const bp = b.basePremium == null ? Infinity : b.basePremium;
      if (ap !== bp) return ap - bp;
      return (a.reg.priority ?? 999) - (b.reg.priority ?? 999) || a.name.localeCompare(b.name);
    });

    const rows = [];
    for (const m of markets) {
      const leader = !m.allDeclined && !m.baseReferred && m.basePremium != null && low != null && m.basePremium === low;
      rows.push({ key: m.slug, row: m.base, market: m, isLeader: leader });
      for (const alt of m.alts) rows.push({ key: `${m.slug}:${alt.scenario.scenario_id}`, row: alt, market: m, isLeader: false });
    }
    return { tableRows: rows, lowest: low, marketCount: markets.length, markets };
  }, [scenarios, addedMarkets, orderedScenarios, rootScenario, registryBySlug, quoteFor]);

  const shownSlugs = useMemo(() => new Set(tableRows.map((r) => r.market.slug)), [tableRows]);
  const addableMarkets = useMemo(
    () => carriers
      .filter((c) => !c.is_cobroker && (c.lines_of_business || []).includes(lob) && !shownSlugs.has(c.slug))
      .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999) || a.name.localeCompare(b.name)),
    [carriers, shownSlugs, lob]
  );

  const presentingCount = useMemo(
    () => scenarios.reduce((n, g) => n + (g.quotes || []).filter((q) => q.presenting).length, 0),
    [scenarios]
  );

  // The selected (client-bound) quote across all scenarios, if any. Drives the
  // L.2 bound state: the cockpit collapses to this quote and the broker places it
  // with the carrier. "bound = paid" — the collapse fires only AFTER payment
  // confirms (deal_stage 'Paid'); a selected-but-unpaid deal stays in the
  // shopping spreadsheet (the client is mid-wizard, not bound yet).
  const selected = useMemo(() => {
    for (const g of scenarios) {
      for (const q of g.quotes || []) {
        // Legacy quotes carry bound state on quotes.status='bound' only — the
        // cockpit must collapse to the bound view for them too (Phase 0: an
        // unrecognized legacy bound left the deal looking shoppable).
        if (q.status === 'selected' || q.status === 'bound' || q.shopping_status === 'selected' || q.shopping_status === 'bound') {
          return { scenario: g, quote: q, reg: registryBySlug.get(q.carrier) || { slug: q.carrier, name: q.carrier, routing_type: 'direct_email' } };
        }
      }
    }
    return null;
  }, [scenarios, registryBySlug]);

  const otherCounts = useMemo(() => {
    const others = markets.filter((m) => m.slug !== selected?.reg?.slug);
    return {
      kept: others.length,
      quoted: others.filter((m) => !m.allDeclined && m.basePremium != null).length,
      declined: others.filter((m) => m.allDeclined).length,
    };
  }, [markets, selected]);

  // Three cockpit states: shopping (spreadsheet) -> bound (paid) -> distribute
  // (paid + placed/issued). expandFromBound drops back to the spreadsheet.
  // Bound requires dealPaid ("bound = paid"): a selected-but-unpaid deal never
  // collapses to bound — it must clear signature + payment in the wizard first.
  // (placed can only be set from the bound state, so distribute implies paid.)
  const placed = !!selected && selected.quote?.placement_status === 'placed';
  const distributeView = !!selected && placed && !expandFromBound;
  const boundView = !!selected && dealPaid && !placed && !expandFromBound;

  // Distribute view data, fetched only when that view is live: the server's
  // paperwork checklist (the documents and the standard set's recipients). A
  // failed read says so on the line; it is never counted as nothing missing.
  // A send, a drop or a ledger change elsewhere on the page re-reads it, as the Paperwork tab does (second Chrome pass, round 2:
  // the standard set went out and the line still said "not yet delivered" until a reload).
  useEffect(() => {
    let alive = true;
    const read = () => getDealPaperwork(dealId).then((r) => { if (alive) { setPaperwork(r); setPaperworkError(null); } })
      .catch((e) => { if (alive) { setPaperwork(null); setPaperworkError(e?.body?.code === 'NO_POLICY_ROW' ? 'this deal has no policy record in TRACON yet' : (e?.body?.error || e?.message || 'failed')); } });
    const on = (ev) => { if (String(ev?.detail?.dealId) === String(dealId)) read(); };
    if (distributeView && dealId) {
      read();
      window.addEventListener(DOCUMENTS_CHANGED_EVENT, on);
      window.addEventListener(LEDGER_CHANGED_EVENT, on);
    }
    return () => { alive = false; window.removeEventListener(DOCUMENTS_CHANGED_EVENT, on); window.removeEventListener(LEDGER_CHANGED_EVENT, on); };
  }, [distributeView, dealId]);

  // What each market said. The decline reason left the quote's own columns in
  // CC PROMPT 23 Part A, so the grid reads it from the one store rather than
  // from a second copy that only existed where a quotes row did.
  useEffect(() => {
    let alive = true;
    if (!dealId) { setMarketDeclines(new Map()); return undefined; }
    listDealMarkets(dealId)
      .then((d) => {
        if (!alive) return;
        setMarketDeclines(new Map((d?.markets || [])
          .filter((m) => m.state === 'declined' && m.carrier_slug)
          .map((m) => [m.carrier_slug, m.declination_reason || null])));
      })
      .catch(() => { if (alive) setMarketDeclines(new Map()); });
    return () => { alive = false; };
  }, [dealId]);

  // A fresh dialog starts empty. Without this the previous market's reasons are
  // pre-ticked on the next one, which is a wrong record one click away.
  useEffect(() => {
    if (declineTarget) { setDeclineReasons([]); setDeclineNotes(''); }
  }, [declineTarget]);

  const run = useCallback(
    async (fn, successMsg, { notifyWorkspace = false } = {}) => {
      setBusy(true);
      try {
        await fn();
        if (successMsg) toast.success(successMsg);
        await refresh();
        if (notifyWorkspace && typeof onChange === 'function') onChange();
      } catch {
        await refresh().catch(() => {});
      } finally {
        setBusy(false);
      }
    },
    [refresh, onChange]
  );

  const handleCreate = useCallback(() => run(() => createSubmission(dealId), 'Submission created. Roster seeded from the deal'), [run, dealId]);
  // "Run API quote". For IAT (the one live API carrier) this fires the real
  // AeroQuote pipeline (/iat-quote → manifest → XML → submit → parse) and lands
  // the parsed rate in the (scenario × IAT) grid cell via createScenarioQuote.
  // Other api-routing carriers (e.g. Global, not yet wired) keep the existing
  // shell-create behavior — scope is IAT only.
  // Global Aerospace SNK — the live-quote twin of the IAT branch below.
  //
  // The shapes differ in one way that matters: IAT either rates or errors, but
  // Global has a THIRD outcome. A referral ("Application In UW Review") or a
  // refusal ("Application Not Offering Terms") still returns a quote number and
  // a full hull/liability option ladder — just no premium. Landing that as a
  // cell would put a $0 row in the comparison and make an underwriter
  // conversation look like a priced market. `hasTerms` is the only safe gate.
  const handleGlobalMaterialize = useCallback(async (scenario, carrier) => {
    setBusy(true);
    try {
      const res = await runGlobalQuote(dealId);
      const q = res?.quote;
      // Our request-side notices + Global's response-side notices. These are the
      // whole point of the mapper's warnings contract (coverage date clamped,
      // pilot experience unmatched, medical limit silently dropped, entity
      // insured unconfirmed) and must never be swallowed. They also persist
      // server-side on the Global quote row (quotes.notes) at capture time.
      const warnings = [...(res?.mapper_warnings || []), ...(q?.warnings || [])].filter(Boolean);
      const showWarnings = () => {
        if (!warnings.length) return;
        toast.warning(`Global: ${warnings.length} thing${warnings.length === 1 ? '' : 's'} to check before presenting`, {
          description: warnings.join(' · '),
          duration: 15000,
        });
      };

      // Refused before submit: an aircraft with no base airport or no runway
      // length null-refs Global's rater into a 500, so the server does not send
      // it. This is a data gap on the deal, and the fix is upstream of Global.
      if (res?.blocked) {
        toast.error(`Global not sent: ${(res.blockers || []).join(' ')}`, { duration: 15000 });
        showWarnings();
        return;
      }

      if (!q) {
        toast.error('Global returned no quote payload');
        return;
      }

      // No terms: referred, declined, or rated at nothing. Surface the state and
      // the reasons; create NO priced cell.
      if (!q.hasTerms) {
        const reasons = (q.referDeclineReasons || []).filter(Boolean).join('; ');
        const label = q.quoteNumber ? ` ${q.quoteNumber}` : '';
        if (q.producerNotLicensed) {
          toast.error(`Global: not licensed to write this risk's state. Global blocks quoting and binding there${reasons ? `. ${reasons}` : ''}.`, { duration: 15000 });
        } else if (q.isDeclined) {
          toast.error(`Global declined${label}${reasons ? `: ${reasons}` : ''}. No quote logged.`, { duration: 15000 });
        } else if (q.isReferred) {
          toast.warning(`Global referred${label} to an underwriter, no terms yet${reasons ? `: ${reasons}` : ''}.`, { duration: 15000 });
        } else {
          toast.error(`Global returned no premium${label}${q.status ? ` (${q.status})` : ''}. No quote logged.`);
        }
        showWarnings();
        return;
      }

      // ── OPTION LADDER ────────────────────────────────────────────────────
      // Global's premiums.total is its DEFAULT hull basis (F. All Risks, the
      // priciest of three). Landing it over-quotes: measured $3,100 on a
      // PA-24-250 where the same quote at G is $2,020. So when the response
      // carries a ladder, hand the pick to the broker and land NOTHING yet —
      // handleLadderConfirm finishes the job.
      if (res?.ladder?.available && res.quote_id) {
        setLadder({
          scenario, carrier, quote: q, quoteId: res.quote_id,
          ladder: res.ladder,
          selection: res.ladder_default_selection || { hullByUnitId: {}, liabilityId: null, medicalIndex: null },
          warnings,
        });
        return;
      }
      // An ambiguous multi-aircraft ladder must not fall back to the default
      // premium — that is the over-quote wearing a different hat.
      if (res?.ladder?.ambiguity) {
        toast.error(`Global ${q.quoteNumber || ''}: ${res.ladder.ambiguity.message}`, { duration: 20000 });
        showWarnings();
        return;
      }

      const p = q.premiums || {};
      // referred rides the CREATE body (mapBodyToQuote already coerces it), so
      // the flag can never be lost between an insert and a follow-up patch —
      // the old two-step needed a fail-closed toast for exactly that gap.
      await createScenarioQuote(submission.id, scenario.scenario_id, {
        carrier: carrier.name,
        carrier_slug: carrier.slug,
        premium: p.total,
        ...(p.hull != null ? { hull_premium: p.hull } : {}),
        ...(p.liability != null ? { liability_premium: p.liability } : {}),
        ...(q.quoteNumber ? { carrier_quote_number: q.quoteNumber } : {}),
        ...(q.isReferred ? { referred: true } : {}),
      });

      await refresh();
      if (typeof onChange === 'function') onChange();

      const dollars = `$${Number(p.total).toLocaleString('en-US')}`;
      // Global's surcharge rides alongside premium; totalAmount is what the
      // client actually pays, so show it when it differs.
      const amount = p.amount != null && p.amount !== p.total
        ? ` (${`$${Number(p.amount).toLocaleString('en-US')}`} with surcharge)`
        : '';
      toast.success(`Global quote ${q.quoteNumber || ''} logged at ${dollars}${amount}${q.isReferred ? ', referred' : ''}`);
      showWarnings();
    } catch (err) {
      const m = err?.body?.error || err?.message || 'Global quote failed';
      if (/no session|manifest/i.test(m)) {
        toast.error('This deal has no session yet, so Global cannot build the submission. Pre-Stage / Initiate it first.');
      } else {
        toast.error(`Global quote failed: ${m}`);
      }
    } finally {
      setBusy(false);
    }
  }, [submission, dealId, refresh, onChange]);

  // Land the broker's ladder pick. The premium is re-priced SERVER-side from the
  // persisted Global quote (/quote/land) rather than from anything this component
  // computed, so what lands is always Global's own per-option premium for the
  // chosen basis. The dialog's running total is display only.
  const handleLadderConfirm = useCallback(async () => {
    if (!ladder) return;
    const { scenario, carrier, quote, quoteId, selection, warnings } = ladder;
    setBusy(true);
    try {
      const priced = await landGlobalSelection(quoteId, selection);
      const body = priced?.landing_body;
      if (!body || body.premium == null) {
        toast.error('Global: could not price that selection');
        return;
      }

      // referred rides the CREATE body, same as the default path.
      await createScenarioQuote(submission.id, scenario.scenario_id, {
        ...body,
        carrier: carrier.name,
        carrier_slug: carrier.slug,
        ...(quote.isReferred ? { referred: true } : {}),
      });

      setLadder(null);
      await refresh();
      if (typeof onChange === 'function') onChange();

      const dollars = `$${Number(body.premium).toLocaleString('en-US')}`;
      const basis = body.option_label ? ` · ${body.option_label}` : '';
      toast.success(`Global quote ${quote.quoteNumber || ''} logged at ${dollars}${basis}${quote.isReferred ? ', referred' : ''}`);
      if (warnings?.length) {
        toast.warning(`Global: ${warnings.length} thing${warnings.length === 1 ? '' : 's'} to check before presenting`, {
          description: warnings.join(' · '),
          duration: 15000,
        });
      }
    } catch (err) {
      const body = err?.body || {};
      toast.error(`Global: ${body.message || body.error || err?.message || 'could not log that selection'}`, { duration: 15000 });
    } finally {
      setBusy(false);
    }
  }, [ladder, submission, refresh, onChange]);

  // Dispatch WITHOUT the readiness gate. Everything below this line assumes the
  // gate already ran (or that the carrier has no preflight).
  const runMaterialize = useCallback(async (scenario, carrier) => {
    if (carrier.slug === 'global_aerospace') {
      return handleGlobalMaterialize(scenario, carrier);
    }
    if (carrier.slug !== 'iat') {
      return run(() => materializeScenario(submission.id, scenario.scenario_id, [{ slug: carrier.slug, name: carrier.name }]),
        `${carrier.name} quote created`, { notifyWorkspace: true });
    }
    setBusy(true);
    try {
      const res = await runIatQuote(dealId);
      // A pilot name the IAT XML printed blank (ruling 4 at the flip): the line is the broker's, never the carrier's.
      for (const w of (Array.isArray(res?.warnings) ? res.warnings : [])) toast.info(w);
      // NOT SENT: the shared pre-submit check (utils/carrier-presubmit) refused.
      if (showPresubmitRefusal(res, 'IAT', navigate)) return;
      const q = res?.quote;
      if (!q?.success || q?.premiums?.total == null) {
        const msg = (q?.errors || []).filter(Boolean).join('; ') || 'IAT could not rate this submission';
        toast.error(`IAT: ${msg}`);
        return;
      }
      const p = q.premiums;
      const created = await createScenarioQuote(submission.id, scenario.scenario_id, {
        carrier: carrier.name,
        carrier_slug: carrier.slug,
        premium: p.total,
        ...(p.hull != null ? { hull_premium: p.hull } : {}),
        ...(p.liability != null ? { liability_premium: p.liability } : {}),
        ...(q.quoteNumber ? { carrier_quote_number: q.quoteNumber } : {}),
      });
      // IAT returned a quote number BUT flagged it: a non-empty <Errors> block
      // (parsed.errors, e.g. "UW Review") means the rate is referred to an
      // underwriter and can NOT auto-bind. Mark the cockpit quote referred so it
      // wears the amber flag instead of reading as a clean bindable quote.
      const referredFor = (q.errors || []).filter(Boolean);
      let referredApplied = !referredFor.length; // nothing to apply → trivially ok
      if (referredFor.length && created?.quote_id) {
        try {
          await patchQuoteShopping(submission.id, scenario.scenario_id, created.quote_id, { referred: true });
          referredApplied = true;
        } catch (e) {
          console.warn('referred flag failed:', e?.message);
        }
      }
      await refresh();
      if (typeof onChange === 'function') onChange();
      const dollars = `$${Number(p.total).toLocaleString('en-US')}`;
      if (referredFor.length && !referredApplied) {
        // Fail CLOSED: the quote is referred (UW review) but the flag did not
        // save, so the grid may show it as a clean, bindable quote. Warn loudly
        // so the broker marks it referred before presenting — never silent.
        toast.error(`IAT quote ${q.quoteNumber || ''} logged at ${dollars}, but it is REFERRED (${referredFor.join('; ')}) and that flag did not save. Mark it referred before presenting.`);
      } else {
        toast.success(`IAT quote ${q.quoteNumber || ''} logged at ${dollars}${referredFor.length ? `, referred (${referredFor.join('; ')})` : ''}`);
      }
    } catch (err) {
      const m = err?.body?.error || err?.message || 'IAT quote failed';
      // No session / no manifest → point to the V2 Pre-Stage / Initiate path,
      // never "old TRACON". IAT needs a session+manifest to build the XML.
      if (/no session|manifest/i.test(m)) {
        toast.error('This deal has no session yet, so IAT cannot build the submission. Pre-Stage / Initiate it first.');
      } else {
        toast.error(`IAT quote failed: ${m}`);
      }
    } finally {
      setBusy(false);
    }
  }, [run, submission, dealId, refresh, onChange, handleGlobalMaterialize, navigate]);

  // ── Dispatch preflight gate (DISPATCH-PREFLIGHT-SPEC) ─────────────────────
  //
  // Nothing reaches a carrier until the deal is ready. The check is a pure
  // server-side dry-run (no carrier call), so a doomed submit costs nothing —
  // and the broker gets the fix list instead of a 4xx from the carrier.
  //
  // Runs ON CLICK rather than for every row on render: each check rebuilds the
  // deal manifest, and the panel would otherwise pay that per API carrier every
  // time it opens.
  //
  // Fail-open by design. If readiness itself errors we still dispatch: the
  // server refuses an unquotable payload anyway (route, handler, and submitQuote
  // all gate), so a broken gate must not become a broken quote button.
  const handleMaterialize = useCallback(async (scenario, carrier) => {
    if (carrier?.routing_type !== 'api') return runMaterialize(scenario, carrier);

    setBusy(true);
    let readiness = null;
    try {
      const res = await getCarrierReadiness(dealId, carrier.slug);
      readiness = res?.readiness || null;
    } catch (e) {
      // Fail-open stays, but the skip is no longer silent (David 2026-08-12):
      // a submission reaching a market with its preflight skipped is a fact
      // the broker hears in the interface, not the console.
      toast.info(`Preflight check could not run (${e?.message || 'error'}). Dispatching to ${carrier?.name || carrier?.slug || 'the carrier'} without it.`);
    } finally {
      setBusy(false);
    }

    if (!readiness || readiness.preflight_supported === false) return runMaterialize(scenario, carrier);
    if (readiness.ready && !(readiness.warnings || []).length) return runMaterialize(scenario, carrier);

    setGate({ scenario, carrier, readiness });
    return undefined;
  }, [dealId, runMaterialize]);

  const gateRecheck = useCallback(async () => {
    if (!gate) return;
    setBusy(true);
    try {
      const res = await getCarrierReadiness(dealId, gate.carrier.slug);
      const readiness = res?.readiness || null;
      if (readiness?.ready && !(readiness.warnings || []).length) {
        setGate(null);
        return runMaterialize(gate.scenario, gate.carrier);
      }
      setGate((g) => (g ? { ...g, readiness } : g));
      if (readiness && !readiness.ready) {
        toast.warning(`Still ${readiness.blockers.length} to fix before ${gate.carrier.name} can quote`);
      }
    } catch (e) {
      toast.error(`Readiness check failed: ${e?.message || 'unknown error'}`);
    } finally {
      setBusy(false);
    }
    return undefined;
  }, [gate, dealId, runMaterialize]);

  const gateProceed = useCallback(() => {
    if (!gate) return undefined;
    const { scenario, carrier } = gate;
    setGate(null);
    return runMaterialize(scenario, carrier);
  }, [gate, runMaterialize]);

  const handleApprove = useCallback((pilot) =>
    run(() => patchRoster(submission.id, {
      pilots: (submission.pilots || []).map((p) => ({
        contact_id: p.contact_id,
        membership: p.contact_id === pilot.contact_id ? 'rated' : p.membership,
        ...(p.contact_id !== pilot.contact_id && p.membership === 'excluded' ? { exclude_reason: p.exclude_reason || '' } : {}),
      })),
    }), 'Pilot back on the approved roster'), [run, submission]);
  const handleExcludeConfirm = useCallback(() => {
    const pilot = excludePilot; const reason = excludeReason.trim();
    setExcludePilot(null); setExcludeReason('');
    if (!pilot) return;
    run(() => patchRoster(submission.id, {
      pilots: (submission.pilots || []).map((p) => ({
        contact_id: p.contact_id,
        membership: p.contact_id === pilot.contact_id ? 'excluded' : p.membership,
        ...(p.contact_id === pilot.contact_id ? { exclude_reason: reason } : p.membership === 'excluded' ? { exclude_reason: p.exclude_reason || '' } : {}),
      })),
    }), 'Named exclusion recorded. Prints on the binding application'); }, [run, submission, excludePilot, excludeReason]);
  const handleToggleAircraft = useCallback((aircraft) =>
    run(() => patchRoster(submission.id, {
      aircraft: (submission.aircraft || []).map((a) => ({
        tail_number: a.tail_number,
        included: a.tail_number === aircraft.tail_number ? !(a.included !== false) : a.included !== false,
      })),
    }), null), [run, submission]);

  const buildScope = useCallback((scenario, carrier, quote) => ({
    submissionId: submission.id,
    scenarioId: scenario.scenario_id,
    quoteId: quote?.quote_id || null,
    crmQuotationId: quote?.crm_quotation_id || null,
    carrier: { slug: carrier.slug, name: carrier.name },
    scenario: { scenario_label: scenario.scenario_label, coverage: scenario.coverage, aircraft_hull: scenario.aircraft_hull },
    includedTails: (submission.aircraft || []).filter((a) => a.included !== false).map((a) => String(a.tail_number).trim().toUpperCase()),
    // Approved roster the quote-logging APR matrix attaches to (per pilot, per
    // tail). Stored value is still 'rated' pending the coordinated rename.
    ratedPilots: (submission.pilots || [])
      .filter((p) => p.membership === 'rated')
      .map((p) => ({ contact_id: String(p.contact_id), name: p.name || '' })),
  }), [submission]);
  const openEditor = useCallback((scenario, carrier, quote) => {
    if (!submission) return;
    setEditorTarget({ scenario, carrier, quote, scope: buildScope(scenario, carrier, quote) });
  }, [submission, buildScope]);

  // Inline premium commit: edit an existing quote, or create a manual one on the
  // market (the route derives the schedule + coverage from the scenario).
  const handlePremium = useCallback((row, premium) => {
    if (!submission) return;
    const { scenario, quote, market } = row;
    if (quote?.quote_id) {
      run(() => patchScenarioQuote(submission.id, scenario.scenario_id, quote.quote_id, { premium }), 'Premium logged', { notifyWorkspace: true });
    } else {
      // Resolve the carrier off the row's OWN market (always on screen): prefer the
      // registry entry, fall back to the market's own slug/name (markets carry both
      // even when unregistered). Send name + slug; the route accepts carrier_slug.
      const reg = market?.reg || registryBySlug.get(market?.slug) || market || {};
      const carrierName = reg.name || market?.name || null;
      const carrierSlug = reg.slug || market?.slug || null;
      run(() => createScenarioQuote(submission.id, scenario.scenario_id, { carrier: carrierName, carrier_slug: carrierSlug, premium }), 'Quote logged', { notifyWorkspace: true });
    }
  }, [run, submission, registryBySlug]);

  const cloneChildAndLog = useCallback(async (market, label) => {
    if (!submission || !rootScenario) return;
    setBusy(true);
    try {
      const res = await cloneScenario(submission.id, rootScenario.scenario_id, { parent_scenario_id: rootScenario.scenario_id, label });
      await refresh();
      const newScn = res?.scenario;
      if (newScn) {
        const scenarioLike = { scenario_id: newScn.id, scenario_label: newScn.label, coverage: newScn.coverage, aircraft_hull: newScn.aircraft_hull };
        setEditorTarget({ scenario: scenarioLike, carrier: market.reg, quote: null, scope: buildScope(scenarioLike, market.reg, null) });
      }
    } catch { await refresh().catch(() => {}); } finally { setBusy(false); }
  }, [submission, rootScenario, refresh, buildScope]);
  const handleAlternate = useCallback((market) => cloneChildAndLog(market, `${rootScenario?.scenario_label || 'Base'} · alt`), [cloneChildAndLog, rootScenario]);
  // handleSixMonth is GONE (2026-07-31). It cloned a whole SCENARIO labelled
  // '6-mo term', so a term variant lived a scenario, a quote and an option deep:
  // three levels of duplication for one number, and the two copies were free to
  // disagree on coverage, which is how Black Bear Aero ended with a 12-month row
  // at hull $160,000 beside a six-month one at $155,000.
  //
  // A six-month offer is a second PREMIUM under one quote. The quote wizard's
  // offer step is the only door (5c7cb68): it adds a term-variant OPTION that
  // inherits the annual's coverage and cannot drift from it.
  //
  // cloneChildAndLog STAYS: handleAlternate uses it, and an alternate scenario
  // is a real thing rather than a duplicate of one.

  // "Log decline" opens a dialog to capture every reason the carrier gave
  // (canonical, plural) plus optional detail before flipping the cell to
  // declined. The fields are cleared by the effect that watches declineTarget.
  const handleDecline = useCallback((scenario, quote) => {
    setDeclineTarget({ scenario, quote });
  }, []);
  // "Set sent date" backdates the market's went-to-market date (item 4) so the
  // row aging chip and the carrier.submission_stale clock run from the real send
  // date, not the day the broker happened to log the premium.
  const handleSetMarketDate = useCallback((scenario, market, quote) => {
    setMarketDateVal(quote?.went_to_market_at || new Date().toISOString().slice(0, 10));
    setMarketDateTarget({ scenario, market, quote });
  }, []);
  const handleMarketDateConfirm = useCallback(() => {
    const t = marketDateTarget;
    if (!t?.quote?.quote_id || !marketDateVal) { setMarketDateTarget(null); return; }
    setMarketDateTarget(null);
    run(() => patchQuoteShopping(submission.id, t.scenario.scenario_id, t.quote.quote_id, { went_to_market_at: marketDateVal }), 'Sent date updated');
  }, [run, submission, marketDateTarget, marketDateVal]);
  // "Delete quote" yanks a mis-entered or duplicate quote: removes the domain
  // quote (+options) and its CRM mirror. Confirmed via dialog — irreversible.
  const handleDeleteStart = useCallback((scenario, market, quote) => setDeleteTarget({ scenario, market, quote }), []);
  const handleDeleteConfirm = useCallback(() => {
    const t = deleteTarget; setDeleteTarget(null);
    if (!t?.quote?.quote_id) return;
    run(() => deleteQuote(t.quote.quote_id), 'Quote deleted', { notifyWorkspace: true });
  }, [run, deleteTarget]);
  const handleDeclineConfirm = useCallback(() => {
    const t = declineTarget; if (!t) return;
    const reasons = declineReasons;
    const detail = declineNotes.trim() || null;
    setDeclineTarget(null);
    run(async () => {
      await patchQuoteShopping(submission.id, t.scenario.scenario_id, t.quote.quote_id, {
        shopping_status: 'declined',
        declination: { reasons, detail },
      });
      // The reason lives on the market now, so the cell's label comes from
      // there. Refresh it rather than guessing optimistically.
      if (dealId) {
        const d = await listDealMarkets(dealId).catch(() => null);
        if (d) setMarketDeclines(new Map((d.markets || [])
          .filter((m) => m.state === 'declined' && m.carrier_slug)
          .map((m) => [m.carrier_slug, m.declination_reason || null])));
      }
    }, 'Decline logged');
  }, [run, submission, dealId, declineTarget, declineReasons, declineNotes]);

  // Drop a carrier PDF onto a market row → upload + file it (L.4). When the row
  // already has a quote, the doc links to it (matched_quote_id) and appears as
  // "doc on file"; an empty row files to the deal (broker then logs the premium
  // to attach it to the market). Not auto-parse — the upload/attach half only.
  const handleDropPdf = useCallback((scenario, market, quote, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Drop a PDF quote document'); return; }
    run(async () => {
      try {
        await dropQuotePdf(dealId, quote?.quote_id || null, file);
      } catch (e) {
        toast.error(`Upload failed: ${e.message}`);
        throw e; // run() refreshes; the rethrow keeps the success toast from firing
      }
    }, quote?.quote_id ? `Filed to ${market?.name || 'this market'}` : 'Filed to the deal, log the premium to attach it here');
  }, [run, dealId]);

  const handleBindStart = useCallback((scenario, carrier, quote) => {
    // Defense in depth: even if a Bind entry point slips through, a referred
    // quote is never bindable.
    if (quote?.referred) { toast.error('This quote is referred to underwriter and cannot be bound until it is cleared.'); return; }
    // No-app renewal bind (FLOW 2026-07-11): reset the checkbox each open and
    // seed the summary-of-changes from what this submission knows — named
    // exclusions plus off-applied terms on the winning quote.
    setNoApp(false);
    setNoAppSummary(buildNoAppSeed({ scenario, quote }, submission?.pilots));
    setBindTarget({ scenario, carrier, quote });
  }, [submission]);
  const handleBindConfirm = useCallback(() => {
    const t = bindTarget;
    const skipApp = noApp;
    const summary = noAppSummary.trim();
    setBindTarget(null);
    if (!t) return;
    run(async () => {
      await bindScenarioQuote(submission.id, t.scenario.scenario_id, t.quote.quote_id,
        skipApp ? { no_new_application: true, changes_summary: summary } : {});
      if (skipApp) {
        // Skip the application/signature session entirely: the broker lands
        // in the renewal payment request, prefilled with the summary. The
        // client's acknowledgment at payment is the documented record.
        setPrPrefill({ changesSummary: summary });
        setPrOpen(true);
      } else {
        setBindDone(t);
      }
    }, `Bind recorded: ${t.carrier.name}`, { notifyWorkspace: true });
  }, [run, submission, bindTarget, noApp, noAppSummary]);

  const handlePresentToggle = useCallback((scenario, quote) => {
    // Turning Present ON for a referred quote stops to ask: a referred quote
    // isn't firm (UW review), so presenting it to a client needs a deliberate
    // "did you mean to / has it been reviewed?". Un-presenting never asks.
    if (!quote.presenting && quote.referred) {
      setPresentReviewed(false);
      setPresentReferredTarget({ scenario, quote });
      return;
    }
    run(() => patchQuoteShopping(submission.id, scenario.scenario_id, quote.quote_id, { presenting: !quote.presenting }), null);
  }, [run, submission]);
  // Confirm presenting a referred quote. If the broker says the UW has cleared
  // it, also drop the referred flag (the quote becomes firm) in the same write.
  const handlePresentReferredConfirm = useCallback(() => {
    const t = presentReferredTarget; const reviewed = presentReviewed;
    setPresentReferredTarget(null); setPresentReviewed(false);
    if (!t) return;
    run(() => patchQuoteShopping(submission.id, t.scenario.scenario_id, t.quote.quote_id,
      { presenting: true, ...(reviewed ? { referred: false } : {}) }),
      reviewed ? 'Marked firm and presenting' : 'Presenting (still referred)');
  }, [run, submission, presentReferredTarget, presentReviewed]);
  // "Adjust" from the dialog: jump into the quote editor for this market.
  const handlePresentReferredAdjust = useCallback(() => {
    const t = presentReferredTarget;
    setPresentReferredTarget(null); setPresentReviewed(false);
    if (!t) return;
    const reg = registryBySlug.get(t.quote.carrier) || { slug: t.quote.carrier, name: t.quote.carrier };
    openEditor(t.scenario, reg, t.quote);
  }, [presentReferredTarget, registryBySlug, openEditor]);
  const handlePresentSelected = useCallback(() => {
    setPresentConfirm(false);
    const presenting = scenarios.flatMap((g) => (g.quotes || []).filter((q) => q.presenting).map((q) => ({ scenarioId: g.scenario_id, quoteId: q.quote_id })));
    if (presenting.length === 0) return;
    // The REAL present path (not the Supabase-only shopping lane): per quote,
    // POST /api/tracon/quotes/:id/present sets domain status='presented' +
    // client_visible=true, AWAITS the CRM Quotation mirror (Client_Visible +
    // Status:Presented), and bumps Deal Stage -> Presented (guarded past
    // Binding/Bound). The old patchQuoteShopping({client_visible}) write never
    // left Supabase, so CRM and stage never moved. Failures are loud: run()
    // swallows errors, so we collect and toast them here per quote.
    run(async () => {
      const failed = [];
      for (const p of presenting) {
        try { await presentQuote(p.quoteId); }
        // The server's sentence per quote: a present-gate refusal (422
        // QUOTE_NOT_PRESENTABLE, decision 13) names its own missing list, and
        // it is not a CRM sync failure. On screen, never the console.
        catch (e) { failed.push({ id: p.quoteId, why: e?.body?.error || e?.message || 'failed', gate: e?.body?.code === 'QUOTE_NOT_PRESENTABLE' }); }
      }
      if (failed.length) {
        const n = `${failed.length} of ${presenting.length} quote${presenting.length === 1 ? '' : 's'}`;
        toast.error(failed.every((f) => f.gate)
          ? `${n} not presented: the present check refused ${failed.length === 1 ? 'it' : 'them'}. Nothing was shown for ${failed.length === 1 ? 'it' : 'those'}.`
          : `${n} failed to present. Fix and hit Present selected again; already-presented quotes are safe to re-send.`,
        { duration: Infinity, description: failed.map((f) => f.why).join(' ') });
        throw new Error('present partial failure');
      }
    }, `${presenting.length} quote${presenting.length === 1 ? '' : 's'} presented: client visible, CRM Quotation synced, deal stage moved to Presented. Send to the client as usual`,
      // notifyWorkspace: stage moved on the Deal, so the workspace header must
      // refetch (same as bind/place). Without it the stage chip sits stale
      // until a hard refresh.
      { notifyWorkspace: true });
  }, [run, submission, scenarios]);

  // Place the bound quote with the carrier. L.2 records the placement status +
  // route; the actual transmit (api / email / portal / cobroker remittance) is a
  // deferred follow-on, so the confirm dialog spells out what is recorded vs sent.
  const handlePlaceConfirm = useCallback(() => {
    const t = placeTarget; setPlaceTarget(null);
    if (!t) return;
    const via = placeRoute(t.reg);
    run(() => patchQuoteShopping(submission.id, t.scenario.scenario_id, t.quote.quote_id, {
      placement_status: 'placed', placement_via: via, placed_at: new Date().toISOString(),
    }), `Placement recorded: ${t.reg.name}`, { notifyWorkspace: true });
  }, [run, submission, placeTarget]);
  // Resolve the broker-facing artifact per chip (bound-view chips follow-up,
  // Hanoff repro). application → the signed application the WINDOW counts
  // (round 2, David Q4: the server's signed_application), never the CRM link
  // and never the client-facing Tower URL. binder → the CURRENT approved bundle
  // (inbound_documents via getCurrentApprovedBundle server-side); no approved
  // bundle → open the draft review flow, same semantics as the send-bundle
  // 409. payment → persistent receipt dialog, not a transient toast.
  const handleDoc = useCallback((kind) => {
    const open = (url) => window.open(url, '_blank', 'noopener');
    if (kind === 'application') {
      // The window's file and words (round 2, David Q4), never the CRM link: the server's one answer.
      const sa = dealItem?.signed_application;
      if (sa?.state === 'in_window' && sa.permalink) return open(sa.permalink);
      return toast(sa?.status || 'Could not check the signed application');
    }
    if (kind === 'binder') {
      const b = dealItem?.binder_bundle;
      if (b?.ok && b.permalink) return open(b.permalink);
      setPaperDraftsOpen(true);
      return;
    }
    if (kind === 'payment') {
      setReceiptOpen(true);
    }
  }, [dealItem]);

  const handleLedger = useCallback(() => toast('Money stays in the ledger. See the Trust tab for the policy ledger.'), []);

  const handleQuoteSaved = useCallback(() => { refresh(); if (typeof onChange === 'function') onChange(); }, [refresh, onChange]);
  const handleExcludeStart = useCallback((pilot) => { setExcludeReason(''); setExcludePilot(pilot); }, []);

  if (!dealId) return null;

  const excludeName = excludePilot
    ? excludePilot.name || nameByContact.get(String(excludePilot.contact_id)) || `Contact …${String(excludePilot.contact_id).slice(-4)}`
    : '';
  const ratedCount = (submission?.pilots || []).filter((p) => p.membership === 'rated').length;
  const excludedCount = (submission?.pilots || []).filter((p) => p.membership === 'excluded').length;
  const approvedNames = (submission?.pilots || [])
    .filter((p) => p.membership === 'rated')
    .map((p) => p.name || nameByContact.get(String(p.contact_id)) || '')
    .filter(Boolean).join(', ');
  const handlers = {
    onPremium: handlePremium, onEdit: openEditor, onMaterialize: handleMaterialize, onBind: handleBindStart,
    onPresent: handlePresentToggle, onAlternate: handleAlternate, onDecline: handleDecline,
    onDropPdf: handleDropPdf, onDelete: handleDeleteStart, onSetMarketDate: handleSetMarketDate,
  };
  const dealLabel = dealItem?.named_insured || dealItem?.deal_name || submission?.label || 'Submission';
  const appliedLine = rootScenario ? coverageLine(rootScenario.coverage, rootScenario.aircraft_hull) : null;
  const expiringLine = dealItem?.expiring_carrier ? `expiring carrier: ${dealItem.expiring_carrier}` : null;
  const selectedCoverage = selected
    ? [coverageLine(selected.scenario.coverage, selected.scenario.aircraft_hull), termLabel(selected.quote.term_months)].filter(Boolean).join(' · ')
    : null;
  const issuedEffective = (() => {
    const d = dealItem?.proposed_effective || dealItem?.policy_start || dealItem?.policy_expiration;
    if (!d) return null;
    return formatHumanDate(d);
  })();

  return (
    <>
      <Panel title="Submission Cockpit" count={marketCount ? `${marketCount} markets` : null} footer={loading ? <span className="text-text-dim">Loading…</span> : null}>
        {error ? <p className="mb-2 text-xs text-warning">Could not load submission: {error.message || 'unknown'}</p> : null}

        {!submission && !loading ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-xs italic text-text-dim">No submission yet. Create one to seed the roster and current terms from this deal.</p>
            <Button size="sm" variant="outline" disabled={busy} onClick={handleCreate} className="gap-1.5"><Plus size={14} strokeWidth={2} />Create submission</Button>
          </div>
        ) : null}

        {submission && distributeView ? (
          <DistributeState
            dealLabel={dealLabel} selected={selected} coverage={selectedCoverage} effective={issuedEffective}
            invoice={fmtMoney(selected.quote.premium)} paperwork={paperwork} paperworkError={paperworkError} busy={busy}
            onLedger={handleLedger} onExpand={() => setExpandFromBound(true)} onOpenDocs={onOpenDocs}
          />
        ) : submission && boundView ? (
          <BoundState
            dealLabel={dealLabel} appliedLine={appliedLine} selected={selected} registryBySlug={registryBySlug}
            approvedNames={approvedNames} otherCounts={otherCounts} busy={busy}
            onPlace={() => setPlaceTarget(selected)} onExpand={() => setExpandFromBound(true)} onDoc={handleDoc} onOpenDocs={onOpenDocs}
            artifacts={{
              bundle: dealItem?.binder_bundle || null,
              signedApplication: dealItem?.signed_application || null,
            }}
          />
        ) : submission ? (
          <>
            {selected ? (
              <button type="button" onClick={() => setExpandFromBound(false)}
                className="mb-2 inline-flex items-center gap-1 text-[11px] text-text-dim hover:text-text-muted">
                ← Back to bound summary
              </button>
            ) : null}
            <div className="text-[17px] font-medium text-text">{dealLabel}</div>
            {appliedLine ? (
              <div className="mt-0.5 text-sm text-text-muted">
                <span className="mr-1.5 text-[10px] uppercase tracking-[0.1em] text-text-dim">As applied for</span>
                {appliedLine}
                {expiringLine ? <span className="ml-2 text-[11px] text-text-dim">{expiringLine}</span> : null}
              </div>
            ) : null}

            <RosterStrip submission={submission} nameByContact={nameByContact} busy={busy}
              onApprove={handleApprove} onExcludeStart={handleExcludeStart} onToggleAircraft={handleToggleAircraft} />

            <div className="mt-2.5 mb-1.5 text-[10px] uppercase tracking-[0.12em] text-text-dim">
              Markets · {presentingCount} presenting · sorted by premium
            </div>

            {tableRows.length === 0 ? (
              <p className="mb-2 text-xs italic text-text-dim">No markets yet. Add one to start shopping.</p>
            ) : (
              <div className="overflow-x-auto rounded-card border border-border">
                <table className="w-full min-w-[680px] border-collapse">
                  <colgroup>
                    <col style={{ width: '26%' }} /><col style={{ width: '11%' }} /><col style={{ width: '15%' }} />
                    <col style={{ width: '11%' }} /><col style={{ width: '19%' }} /><col style={{ width: '8%' }} /><col style={{ width: '10%' }} />
                  </colgroup>
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.08em] text-text-dim">
                      <th className="px-2.5 py-2 text-left font-semibold">Market</th>
                      <th className="px-2.5 py-2 text-left font-semibold">Status</th>
                      <th className="whitespace-nowrap px-2.5 py-2 text-right font-semibold text-text">Premium ▲</th>
                      <th className="px-2.5 py-2 text-right font-semibold">Δ vs lowest</th>
                      <th className="px-2.5 py-2 text-left font-semibold">Terms / conditions</th>
                      <th className="px-2.5 py-2 text-center font-semibold">Present</th>
                      <th className="px-2.5 py-2 text-right font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map(({ key, row, market, isLeader }) => (
                      <MarketRow key={key} row={row} market={market} lowest={lowest} isLeader={isLeader} busy={busy} handlers={handlers} declination={marketDeclines.get(row.quote?.carrier) || null} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="mt-2.5 text-[11px] leading-relaxed text-text-dim">
              Type a premium in the cell, or drop a carrier quote PDF on a row, to log it.
              {/* "add 6-mo" was removed from this sentence 2026-08-05. The control
                  it named (handleSixMonth) was deleted 2026-07-31 in 5c7cb68, and
                  the sentence kept advertising it for five days on a surface David
                  can open — which is how he went looking for a door that is not
                  there. A six-month offer is now a term-variant OPTION, added in
                  the quote wizard's Offer step. The other three named here are
                  real and still on the row menu. */}
              <br /><span className="text-text-muted">Open</span> jumps to the quote editor. The row menu holds the occasionals: add alternate, log decline, bind.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" disabled={busy || addableMarkets.length === 0} className="gap-1.5"><Plus size={14} strokeWidth={2} />Add market</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wide">Add market</DropdownMenuLabel>
                  {addableMarkets.map((c) => (
                    <DropdownMenuItem key={c.slug} onSelect={() => setAddedMarkets((prev) => [...prev, c.slug])}>
                      {c.name} <span className="ml-1 text-text-dim">· {channelLabel(c, registryBySlug)}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button size="sm" variant="accent" disabled={busy || presentingCount === 0} onClick={() => setPresentConfirm(true)} className="ml-auto gap-1.5">
                <Send size={14} strokeWidth={2} />Present selected · {presentingCount}
              </Button>
            </div>
          </>
        ) : null}
      </Panel>

      {/* Bind confirmation dialog, opened from the row menu. Approved_Pilots rewritten to the approved roster. */}
      <Dialog open={!!bindTarget} onOpenChange={(o) => !o && setBindTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Bind {bindTarget?.carrier?.name}{fmtMoney(bindTarget?.quote?.premium) ? ` at ${fmtMoney(bindTarget?.quote?.premium)}` : ''}?
            </DialogTitle>
            <DialogDescription>
              This Quotation becomes the policy. Approved Pilots is rewritten to the approved roster
              ({ratedCount} pilot{ratedCount === 1 ? '' : 's'}
              {excludedCount > 0 ? `; ${excludedCount} named exclusion${excludedCount === 1 ? '' : 's'} print on the binding application` : ''}),
              any prior unpaid bind transaction is voided, and the Quotation flips to Bound.{' '}
              {noApp
                ? 'No new application: the signature step is skipped. You review and send the renewal payment request next; the client’s acknowledgment at payment is the record of what they accepted.'
                : 'You finish in the binding wizard (signature, then payment). The cockpit shows Bound once payment confirms.'}
            </DialogDescription>
            {renewalNoAppEligible ? (
              <div className="mt-2 space-y-2 rounded-md border border-border bg-surface-strong px-3 py-2.5">
                <label className="flex items-start gap-2 text-[12.5px] text-text">
                  <input
                    type="checkbox"
                    checked={noApp}
                    onChange={(e) => setNoApp(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    Renewal: no new application required
                    <span className="block text-[11px] text-text-dim">
                      Broker call, per carrier practice. Skips the signature session; goes straight to the renewal payment request.
                    </span>
                  </span>
                </label>
                {noApp ? (
                  <div className="space-y-1">
                    <label className="text-[11px] text-text-muted" htmlFor="noapp-summary">
                      Summary of changes (required; flows into the client acknowledgment)
                    </label>
                    <textarea
                      id="noapp-summary"
                      value={noAppSummary}
                      onChange={(e) => setNoAppSummary(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-border bg-surface px-2 py-1.5 text-[12.5px] text-text"
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {dealPaid ? (
              <p className="mt-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-400">
                This deal is already paid. If the existing bind carries that payment, binding again will be
                refused to protect the ledger. Post-payment changes belong on an endorsement.
              </p>
            ) : null}
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBindTarget(null)}>Cancel</Button>
            <Button variant={dealPaid ? 'destructive' : 'default'} disabled={noApp && !noAppSummary.trim()} onClick={handleBindConfirm}>
              {dealPaid ? 'Attempt bind anyway' : noApp ? 'Bind, then payment request' : 'Bind this quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment receipt — persistent view of the recorded ledger leg
          (bound-view chip follow-up: a receipt, not a transient toast). */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment confirmation</DialogTitle>
            <DialogDescription>Recorded on the policy ledger.</DialogDescription>
          </DialogHeader>
          {dealItem?.payment ? (
            <dl className="grid grid-cols-[110px_1fr] gap-y-1.5 text-sm">
              <dt className="text-text-dim">Amount</dt>
              <dd className="font-mono text-text">{fmtMoney(dealItem.payment.amount) || 'unknown'}</dd>
              <dt className="text-text-dim">Method</dt>
              <dd className="text-text">{dealItem.payment.method || 'unknown'}</dd>
              <dt className="text-text-dim">Status</dt>
              <dd className="text-text">{dealItem.payment.status || 'unknown'}</dd>
              <dt className="text-text-dim">Date</dt>
              <dd className="text-text">
                {dealItem.payment.paid_at
                  ? formatDateOrEmpty(dealItem.payment.paid_at, { month: 'short', day: 'numeric', year: 'numeric' })
                  : 'unknown'}
              </dd>
              <dt className="text-text-dim">Reference</dt>
              <dd className="break-all font-mono text-xs text-text">{dealItem.payment.ref}</dd>
              {dealItem.payment.txn_id ? (
                <>
                  <dt className="text-text-dim">Ledger txn</dt>
                  <dd className="break-all font-mono text-xs text-text-muted">{dealItem.payment.txn_id}</dd>
                </>
              ) : null}
            </dl>
          ) : (
            <p className="text-sm text-text-muted">
              No TRACON payment on file for this deal (carrier-direct or pre-TRACON bind).
            </p>
          )}
          <DialogFooter>
            {typeof onOpenTrust === 'function' ? (
              <Button variant="outline" onClick={() => { setReceiptOpen(false); onOpenTrust(); }}>Open ledger</Button>
            ) : null}
            <Button onClick={() => setReceiptOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review-first flow for the binder chip: no current approved bundle →
          the broker lands in the draft review sheet (same gate as send-bundle). */}
      <PaperDraftsSheet
        dealId={dealId}
        open={paperDraftsOpen}
        onOpenChange={setPaperDraftsOpen}
        onChange={onChange}
      />

      {/* No-app renewal bind hand-off: after the bind, the broker reviews and
          sends the renewal payment request here (prefilled with the summary of
          changes). The send is the broker's explicit click, never automatic. */}
      <PaymentRequestSheet
        dealId={dealId}
        open={prOpen}
        onOpenChange={(o) => { setPrOpen(o); if (!o) setPrPrefill(null); }}
        prefill={prPrefill}
        onChange={onChange}
      />

      <Dialog open={!!bindDone} onOpenChange={(o) => !o && setBindDone(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Binding in progress: {bindDone?.carrier?.name}</DialogTitle>
            <DialogDescription>
              Writeback complete: Approved Pilots reconciled to the approved roster and the Quotation is marked Bound.
              Continue in the binding wizard (signature, payment, binding application). This deal shows Bound here once payment confirms.
              {!towerUrl ? ' No client session found on this deal. Initiate a session from the deal actions to reach the wizard.' : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBindDone(null)}>Done</Button>
            <Button disabled={!towerUrl} className="gap-1.5"
              onClick={() => { if (towerUrl) window.open(towerUrl, '_blank', 'noopener'); setBindDone(null); }}>
              <ExternalLink size={12} strokeWidth={1.5} />Open binding wizard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Place with carrier. Records the placement status + route; the actual
          carrier transmit is a deferred follow-on (api especially is later). */}
      <Dialog open={!!placeTarget} onOpenChange={(o) => !o && setPlaceTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{placeLabel(placeTarget?.reg)}?</DialogTitle>
            <DialogDescription>
              {placeTarget?.reg?.routing_type === 'api'
                ? `${placeTarget?.reg?.name} accepts an API transmit (the push is wiring up). This records the placement now.`
                : placeTarget?.reg?.routing_type === 'portal'
                ? `${placeTarget?.reg?.name} is a portal carrier; a guided browser task will drive the portal (wiring up). This records the placement now.`
                : placeTarget?.reg?.routing_type === 'via_cobroker'
                ? 'Routed through the co-broker: email plus payment remittance advice (wiring up). This records the placement now.'
                : `Generates the carrier application and binding-language email (sending wires up next). This records the placement now.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlaceTarget(null)}>Cancel</Button>
            <Button variant="accent" onClick={handlePlaceConfirm}>Record placement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Presenting a REFERRED quote: confirm intent + optionally mark it firm.
          A referred quote is not bound-ready, so this guards against putting an
          unreviewed UW-review price in front of the client by accident. */}
      <Dialog open={!!presentReferredTarget} onOpenChange={(o) => { if (!o) { setPresentReferredTarget(null); setPresentReviewed(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Present a referred quote?</DialogTitle>
            <DialogDescription>
              {registryBySlug.get(presentReferredTarget?.quote?.carrier)?.name || presentReferredTarget?.quote?.carrier || 'This carrier'} came back
              referred to underwriter (UW review), so it is not firm yet. Did you mean to present it to the client?
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2.5 rounded-card border border-border bg-surface-strong px-3 py-2.5 text-sm text-text-muted">
            <input type="checkbox" checked={presentReviewed} onChange={(e) => setPresentReviewed(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-success" />
            <span>The underwriter has reviewed and cleared it. Mark this quote firm (drops the referred flag).</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPresentReferredTarget(null); setPresentReviewed(false); }}>Cancel</Button>
            <Button variant="outline" onClick={handlePresentReferredAdjust}>Open quote to adjust</Button>
            <Button variant="accent" onClick={handlePresentReferredConfirm}>
              {presentReviewed ? 'Mark firm and present' : 'Present anyway'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global option ladder — the broker picks the hull basis before any
          premium lands, so Global's default (priciest) basis is never assumed. */}
      <GlobalLadderDialog
        open={!!ladder}
        onOpenChange={(o) => { if (!o) setLadder(null); }}
        ladder={ladder?.ladder}
        selection={ladder?.selection}
        onSelectionChange={(sel) => setLadder((l) => (l ? { ...l, selection: sel } : l))}
        busy={busy}
        onConfirm={handleLadderConfirm}
      />

      {/* Dispatch preflight gate — red blockers stop the fire entirely; an
          amber-only result quotes on one acknowledgement. */}
      <ReadinessDialog
        open={!!gate}
        onOpenChange={(o) => { if (!o) setGate(null); }}
        carrierName={gate?.carrier?.name || 'Carrier'}
        readiness={gate?.readiness}
        busy={busy}
        onRecheck={gateRecheck}
        onProceed={gateProceed}
      />

      <Dialog open={presentConfirm} onOpenChange={setPresentConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Present {presentingCount} quote{presentingCount === 1 ? '' : 's'} to the client?</DialogTitle>
            <DialogDescription>
              Marks the checked quotes presented: client visible, CRM Quotation status synced, and the deal stage
              moves to Presented. Sending the proposal stays manual. Email it from Gmail or the portal as usual.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPresentConfirm(false)}>Cancel</Button>
            <Button variant="accent" onClick={handlePresentSelected}>Mark presented</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!excludePilot} onOpenChange={(o) => { if (!o) { setExcludePilot(null); setExcludeReason(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Named exclusion: {excludeName}</DialogTitle>
            <DialogDescription>Drops the pilot from the approved quote composition. No PHF required; the exclusion prints on the binding application.</DialogDescription>
          </DialogHeader>
          <Input value={excludeReason} onChange={(e) => setExcludeReason(e.target.value)} placeholder="Reason (e.g. no longer flies this aircraft)" autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setExcludePilot(null); setExcludeReason(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleExcludeConfirm}>Exclude pilot</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete quote</DialogTitle>
            <DialogDescription>
              This removes {registryBySlug.get(deleteTarget?.quote?.carrier)?.name || deleteTarget?.market?.name || 'this market'}&rsquo;s quote from the cockpit{deleteTarget?.quote?.crm_quotation_id ? ' and deletes its CRM Quotation' : ''}. This can&rsquo;t be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="accent" disabled={busy} onClick={handleDeleteConfirm} className="gap-1.5">
              <Trash2 size={14} strokeWidth={1.7} />Delete quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!declineTarget} onOpenChange={(o) => { if (!o) setDeclineTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log decline: {registryBySlug.get(declineTarget?.quote?.carrier)?.name || declineTarget?.quote?.carrier || 'this market'}</DialogTitle>
            <DialogDescription>
              Pick every reason they gave. The second one is routinely the one that matters, and this is what the market suggestions learn from.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <fieldset className="space-y-1.5">
              <legend className="text-xs font-medium uppercase tracking-wide text-text-muted">Why did they pass?</legend>
              {DECLINE_REASONS.map((c) => (
                <label key={c.value} className="flex cursor-pointer items-center gap-2 text-sm text-text">
                  <input
                    type="checkbox"
                    checked={declineReasons.includes(c.value)}
                    onChange={() => setDeclineReasons((prev) => prev.includes(c.value) ? prev.filter((x) => x !== c.value) : [...prev, c.value])}
                    className="accent-accent"
                  />
                  {c.label}
                </label>
              ))}
            </fieldset>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
                {requiresDetail(declineReasons) ? 'Detail (required for Other)' : 'Notes (optional)'}
              </span>
              <textarea
                rows={2}
                value={declineNotes}
                onChange={(e) => setDeclineNotes(e.target.value)}
                placeholder="e.g. UW wants 25 hrs make/model before reconsidering"
                className="mt-1.5 flex w-full resize-none rounded-card border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-dim focus:border-accent focus:outline-none"
              />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={!declineReasons.length || (requiresDetail(declineReasons) && !declineNotes.trim())}
              onClick={handleDeclineConfirm}
            >Log decline</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set sent date (item 4): backdate when this market went to the carrier. */}
      <Dialog open={!!marketDateTarget} onOpenChange={(o) => { if (!o) setMarketDateTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set sent date: {registryBySlug.get(marketDateTarget?.quote?.carrier)?.name || marketDateTarget?.quote?.carrier || 'this market'}</DialogTitle>
            <DialogDescription>
              The date this market went to the carrier. Backdate it to the real send date — the row aging and the carrier follow-up clock both run from here.
            </DialogDescription>
          </DialogHeader>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Went to market</span>
            <input
              type="date"
              value={marketDateVal}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setMarketDateVal(e.target.value)}
              autoFocus
              className="mt-1.5 flex min-h-[40px] w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text focus:border-accent focus:outline-none"
            />
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarketDateTarget(null)}>Cancel</Button>
            <Button disabled={!marketDateVal} onClick={handleMarketDateConfirm}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuoteEntrySheet
        dealId={dealId}
        open={!!editorTarget}
        onOpenChange={(o) => !o && setEditorTarget(null)}
        seedQuote={editorTarget?.quote ? { carrier: editorTarget.carrier?.name, premium: editorTarget.quote.premium } : undefined}
        scenarioScope={editorTarget?.scope || null}
        onSaved={handleQuoteSaved}
      />

    </>
  );
}
